import { UserRole } from '@/providers';
import { stateSetter } from '@/utils';
// Import DIRECT (nu prin barrel-ul '@/components') — evită ciclul hooks ↔ components.
import { IServerColumnConfig } from '@/components/table/column-config';

type UserRoleType = 'user' | 'admin' | 'superadmin' | 'moderator' | 'fps' | string;
export type AppUserStatusKey = 'active' | 'blocked' | 'muted' | 'unknown';

export interface IUserRestrict {
	at: number;
	reason: string;
	author: string;
	until: number;
}

interface IRestrictDetails {
	at: number;
	author: string;
	role: string | UserRole;
	reason?: string;
	until?: number;
}

export interface IRestrictObj {
	reason: string;
	until: number;
}

// GET api/users/:id/sanctions — sancțiunile (restricții / blocări) unui utilizator, pentru banner-ele din profil.
export interface IUserSanction {
	id: number;
	userId: number;
	typeId: number;
	reason?: string | null;
	startedAt?: string | null;
	expiresAt?: string | null;
	isActive: boolean;
	liftedAt?: string | null;
	liftedById?: number | null;
	performedById?: number | null;
	performedBy?: IUserLookup | null;
	liftedBy?: IUserLookup | null;
}

export interface IUserLookup {
	id: number;
	firstName?: string | null;
	lastName?: string | null;
}

export interface IUserSanctionsResponse {
	items?: IUserSanction[];
	pageNumber?: number;
	totalPages?: number;
	totalCount?: number;
	orderBy?: string | null;
	ascending?: boolean;
	hasPreviousPage?: boolean;
	hasNextPage?: boolean;
}

// GET api/users/:id/affinities — semnalele de afinitate ale unui utilizator (tab „Afinități").
interface IAffinityRef {
	id: number;
	name: string;
	weight: number;
}

interface IUserAffinitySummary {
	topType?: IAffinityRef | null;
	topStage?: IAffinityRef | null;
	topAdministration?: IAffinityRef | null;
	topLevel?: IAffinityRef | null;
	topArea?: IAffinityRef | null;
}

export interface IUserAffinityValue {
	id: number;
	name: string;
	weight: number;
	share: number;
	lastTouchedAt?: string | null;
	color?: string | null;
	county?: string | null;
}

export interface IUserAffinityDimension {
	dimension: string;
	totalWeight: number;
	distinctValues: number;
	values: IUserAffinityValue[];
}

export interface IUserAffinities {
	userId: number;
	fullName?: string;
	generatedAt?: string;
	totalWeight: number;
	distinctValues: number;
	lastActivityAt?: string | null;
	summary?: IUserAffinitySummary | null;
	dimensions: IUserAffinityDimension[];
}

export type IRestrictEvent = 'mute' | 'block' | 'unblock' | 'unmute';
export interface IRestrictionHistory {
	event: IRestrictEvent;
	details: IRestrictDetails;
}

interface IAppUserFsp {
	id: number;
	metadata: {
		institution: string;
		level: 'local' | 'county' | string;
	};
}

export interface IAppUserStatus {
	id: number;
	name: string;
}

interface IAppUserPermission {
	id: number;
	key: string;
	module: string;
}

interface IAppUserRole {
	id: number;
	name: string;
	// Eticheta localizată de afișare (BE); `name` rămâne identificatorul intern.
	label?: string;
	description?: string;
	permissions?: IAppUserPermission[];
}

interface IAppUserAdministration {
	id?: number;
	[key: string]: unknown;
}

// Administrațiile la care s-a ABONAT cetățeanul (BE: `subscribedAdministrations`) —
// distincte de `administrations` (apartenența web-userilor). Sursa coloanei „Administrații"
// din tabelul de cetățeni.
interface IAppUserSubscribedAdministration {
	id: number;
	name: string;
	code?: string;
	level?: { id: number; name: string } | null;
	subscribedAt?: string;
}

// Locația cetățeanului (BE: `location` — null până la onboarding/setare).
interface IAppUserLocation {
	countyId?: number | null;
	uatId?: number | null;
	county?: { id: number; name: string } | null;
	uat?: { id: number; name: string } | null;
}

export interface IAppUser {
	id: number;
	email: string;
	firstName: string;
	lastName: string;
	phoneCountryCode?: string | null;
	phone: string | null;
	loginAttempts?: number;
	role?: UserRoleType;
	roles?: IAppUserRole[];
	provider?: string;
	status: IAppUserStatus | string;
	blockMuteHistory?: IRestrictionHistory[];
	muted?: boolean;
	blocked?: boolean;
	createdAt: string;
	updatedAt: string;
	lastLoggedInAt?: string | null;
	/** URL simplu (istoric) sau obiectul de asset ({ id, fileName, publicUrl }). */
	avatar?: string | { id?: number; fileName?: string; publicUrl?: string | null } | null;
	hasPhoneVerified?: boolean;
	isOnboarded?: boolean;
	administrations?: IAppUserAdministration[];
	/** Administrațiile la care s-a abonat cetățeanul (sursa coloanei „Administrații"). */
	subscribedAdministrations?: IAppUserSubscribedAdministration[];
	fsps?: IAppUserFsp[];
	/** Locația reală din BE (obiect imbricat) — null până o setează cetățeanul. */
	location?: IAppUserLocation | null;
	// Câmpuri plate legacy — păstrate ca fallback dacă BE le-ar trimite direct.
	county?: string;
	uat?: string;
}

const STATUS_MAP: Record<string, AppUserStatusKey> = {
	activ: 'active',
	active: 'active',
	blocat: 'blocked',
	blocked: 'blocked',
	restrictionat: 'muted',
	restricționat: 'muted',
	muted: 'muted'
};

export const getAppUserId = (user?: IAppUser | null): number => user?.id ?? 0;

export const getAppUserStatusKey = (user?: IAppUser | null): AppUserStatusKey => {
	const rawStatus = typeof user?.status === 'string' ? user.status : user?.status?.name;

	if (!rawStatus) return 'unknown';

	return STATUS_MAP[rawStatus.toLowerCase()] ?? 'unknown';
};

export const isAppUserBlocked = (user?: IAppUser | null): boolean =>
	user?.blocked ?? getAppUserStatusKey(user) === 'blocked';

export const isAppUserMuted = (user?: IAppUser | null): boolean =>
	user?.muted ?? getAppUserStatusKey(user) === 'muted';

export const buildAppUserStatus = (
	name: IAppUserStatus['name'],
	prevStatus?: IAppUser['status']
): IAppUserStatus => ({
	id: typeof prevStatus === 'object' && prevStatus !== null ? prevStatus.id : 0,
	name
});

// Update prin PATCH → trimitem obiectul complet al utilizatorului.
export interface IUpdateAppUser {
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	phoneCountryCode?: string;
	roleId?: number;
	administrationIds?: number[];
	/** Id-ul asset-ului de avatar (imaginea se urcă întâi în assets — logica administrațiilor). */
	avatarId?: number | null;
	// Județ + UAT — trimise DOAR la trecerea în rol de moderator (BE le cere obligatorii pt.
	// moderator, iar la editările de profil ale unui moderator existent BE face fallback pe
	// valorile deja declarate). Absente pentru rolurile non-moderator.
	countyId?: number;
	uatId?: number;
}

export interface AppUsersReturnType<T extends IAppUser | IAppUser[]> {
	data: T;
	setData: stateSetter<T>;
	loading: boolean;
	loadingCTA: boolean;
	loadingUpdate: boolean;
	loadingResetPassword: boolean;
	loadingMute: boolean;
	loadingStatus: boolean;
	getAppUsers: (pageParam: number, itemsParam: number) => Promise<void>;
	getAppUser: (userId: number) => void;
	removeUser: (userId: number, onSuccess?: () => void) => Promise<void>;
	editUser: (
		userId: number | string,
		updatedObject: IUpdateAppUser,
		onSuccess?: () => void
	) => Promise<void>;
	changeRole: (
		userId: number | string,
		newRoleId: number,
		oldRoleId?: number,
		onSuccess?: () => void
	) => Promise<void>;
	resetPassword: (userId: number | string, onSuccess?: () => void) => Promise<void>;
	muteUser: (
		userId: number | string,
		restrictObj: IRestrictObj,
		onSuccess?: () => void
	) => Promise<void>;
	unmuteUser: (userId: number | string, onSuccess?: () => void) => Promise<void>;
	changeUserStatus: (
		userId: number | string,
		statusId: number,
		onSuccess?: () => void
	) => Promise<void>;
	// Sancțiunile utilizatorului (se cer doar dacă e restricționat / blocat) — pentru banner-ele din profil.
	sanctions: IUserSanction[];
	loadingSanctions: boolean;
	getUserSanctions: (userId: number | string) => Promise<IUserSanction[]>;
	// Afinitățile utilizatorului (tab „Afinități").
	affinities: IUserAffinities | null;
	loadingAffinities: boolean;
	getUserAffinities: (userId: number | string) => Promise<IUserAffinities | null>;
	page: number;
	items: number;
	setPage: stateSetter<number>;
	setItems: stateSetter<number>;
	totalPages: number;
	totalItems: number;
	/**
	 * Configurația coloanelor din BE (GET api/users/table-config) — comună tabelelor de
	 * utilizatori (web + cetățeni). Cerută doar când `withTableConfig` e activ; null până
	 * se încarcă sau dacă endpointul eșuează (tabelul cade pe coloanele locale).
	 */
	tableConfig: IServerColumnConfig[] | null;
	loadingTableConfig: boolean;
}
